import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Download, ChevronLeft, ChevronRight, AlertTriangle, Sparkles, Loader2, X } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ReportChartRow, BarCard, asSeries } from './ReportCharts';

const fmtDate = v => v ? v.slice(0, 10) : '—';
const fmtCurrency = v => `$${parseFloat(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const RISK = {
  critical: { badge: 'bg-red-100 text-red-700',    bar: 'bg-red-500' },
  high:     { badge: 'bg-amber-100 text-amber-700', bar: 'bg-amber-500' },
  medium:   { badge: 'bg-yellow-100 text-yellow-700', bar: 'bg-yellow-400' },
  low:      { badge: 'bg-emerald-100 text-emerald-700', bar: 'bg-emerald-500' },
};

export default function FundDepletion() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [threshold, setThreshold] = useState('80');
  const [riskFilter, setRiskFilter] = useState('all');
  const [page, setPage]       = useState(1);
  const [aiModal, setAiModal] = useState(false);
  const [aiPreds, setAiPreds] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const limit = 25;

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ search, threshold });
    api.get(`/reports/fund-depletion?${p}`)
      .then(r => setRows(r.data))
      .catch(() => toast.error('Failed to load depletion data'))
      .finally(() => setLoading(false));
  }, [search, threshold]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, threshold, riskFilter]);

  const filtered = riskFilter === 'all' ? rows : rows.filter(r => r.risk === riskFilter);
  const totalPages = Math.max(1, Math.ceil(filtered.length / limit));
  const from = filtered.length === 0 ? 0 : (page - 1) * limit + 1;
  const to   = Math.min(page * limit, filtered.length);
  const pageData = filtered.slice(from > 0 ? from - 1 : 0, to);

  const stats = {
    critical: rows.filter(r => r.risk === 'critical').length,
    high:     rows.filter(r => r.risk === 'high').length,
    medium:   rows.filter(r => r.risk === 'medium').length,
    low:      rows.filter(r => r.risk === 'low').length,
  };

  const runAiForecast = async () => {
    const atRisk = rows.filter(r => r.risk === 'critical' || r.risk === 'high').slice(0, 10);
    if (!atRisk.length) return toast.error('No high-risk funds to analyse');
    setLoadingAi(true);
    setAiModal(true);
    setAiPreds(null);
    try {
      const { data } = await api.post('/ai/fund-depletion-forecast', { funds: atRisk });
      setAiPreds(data.predictions);
    } catch { toast.error('AI forecast failed'); setAiModal(false); }
    finally { setLoadingAi(false); }
  };

  function downloadCSV() {
    if (!filtered.length) return;
    const cols = ['Client', 'NDIS Number', 'Fund', 'Fund Amount', 'Spent', 'Remaining', '% Used', 'Daily Burn', 'Days Remaining', 'Forecast Depletion', 'Plan End', 'Risk'];
    const data = filtered.map(r => [r.client_name, r.ndis_number || '', r.fund_name, r.fund_amount, r.spent_amount, r.remaining, r.pct_used, r.daily_burn, r.days_remaining ?? '', fmtDate(r.depletion_date), fmtDate(r.plan_end_date), r.risk]);
    const csv  = [cols, ...data].map(r => r.join(',')).join('\n');
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `fund_depletion_${new Date().toISOString().slice(0,10)}.csv`; a.click();
  }

  return (
    <div>
      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-4 pt-4">
        {Object.entries(stats).map(([risk, count]) => (
          <button key={risk} onClick={() => setRiskFilter(riskFilter === risk ? 'all' : risk)}
            className={`rounded-xl border p-3 text-left transition-all ${riskFilter === risk ? 'ring-2 ring-blue-500 border-blue-300' : 'border-slate-200 hover:border-slate-300'}`}>
            <div className={`text-xs font-semibold uppercase mb-1 ${RISK[risk]?.badge.split(' ')[1]}`}>{risk}</div>
            <div className="text-2xl font-bold text-slate-800">{count}</div>
          </button>
        ))}
      </div>

      {/* Charts */}
      {rows.length > 0 && (
        <div className="px-4 pt-3">
          <ReportChartRow>
            <BarCard title="Closest to depletion (% used)" data={asSeries(rows, 'client_name', 'pct_used', { limit: 12 })} color="#EF4444" horizontal />
          </ReportChartRow>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200 flex-wrap mt-3">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Search client..." value={search}
            onChange={e => setSearch(e.target.value)}
            className="border border-slate-300 rounded pl-8 pr-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <div className="flex items-center gap-1.5 text-sm text-slate-600">
          <AlertTriangle size={14} className="text-amber-500" />
          High-risk threshold:
          <select value={threshold} onChange={e => setThreshold(e.target.value)}
            className="border border-slate-300 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="60">60% used</option>
            <option value="70">70% used</option>
            <option value="80">80% used</option>
            <option value="90">90% used</option>
          </select>
        </div>

        <div className="flex-1" />
        <span className="text-sm text-slate-500">{filtered.length} fund{filtered.length !== 1 ? 's' : ''}</span>
        <button onClick={runAiForecast} disabled={loadingAi}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-violet-200 bg-violet-50 hover:bg-violet-100 rounded text-violet-700 text-xs font-semibold disabled:opacity-60">
          {loadingAi ? <Loader2 size={13} className="animate-spin"/> : <Sparkles size={13}/>}
          AI Forecast
        </button>
        <button onClick={downloadCSV} className="p-1.5 border border-slate-300 rounded text-slate-500 hover:bg-slate-50" title="Download CSV">
          <Download size={15} />
        </button>
      </div>

      {/* Table */}
      <div className="bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              {['Client', 'Fund', '% Used', 'Fund Amount', 'Spent', 'Remaining', 'Daily Burn', 'Days Left', 'Forecast Depletion', 'Plan End', 'Risk'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : !pageData.length ? (
              <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-400">No records found</td></tr>
            ) : pageData.map((r, i) => {
              const pct = parseFloat(r.pct_used || 0);
              const risk = RISK[r.risk] || RISK.low;
              return (
                <tr key={r.id || i} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-4 py-2.5 font-medium whitespace-nowrap">
                    <Link to={`/clients/${r.client_id}`} className="text-blue-600 hover:underline">{r.client_name}</Link>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{r.fund_name || '—'}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="w-14 bg-slate-100 rounded-full h-1.5">
                        <div className={`h-1.5 rounded-full ${risk.bar}`} style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                      <span className="text-xs font-medium text-slate-700">{pct.toFixed(1)}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-600 whitespace-nowrap">{fmtCurrency(r.fund_amount)}</td>
                  <td className="px-4 py-2.5 text-right text-slate-600 whitespace-nowrap">{fmtCurrency(r.spent_amount)}</td>
                  <td className="px-4 py-2.5 text-right font-medium text-slate-700 whitespace-nowrap">{fmtCurrency(r.remaining)}</td>
                  <td className="px-4 py-2.5 text-right text-slate-500 whitespace-nowrap">{fmtCurrency(r.daily_burn)}/day</td>
                  <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">
                    {r.days_remaining != null ? `${r.days_remaining}d` : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap font-medium">{fmtDate(r.depletion_date)}</td>
                  <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmtDate(r.plan_end_date)}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${risk.badge}`}>{r.risk}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-slate-200 text-sm text-slate-600 flex-wrap gap-2">
        <span>Showing {from}–{to} of {filtered.length} funds</span>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1}
            className="p-1.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40">
            <ChevronLeft size={14} />
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pg = totalPages <= 5 ? i + 1 : page <= 3 ? i + 1 : page + i - 2;
            if (pg > totalPages) return null;
            return (
              <button key={pg} onClick={() => setPage(pg)}
                className={`w-7 h-7 rounded text-xs font-medium border ${pg === page ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                {pg}
              </button>
            );
          })}
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page >= totalPages}
            className="p-1.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40">
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* AI Forecast Modal */}
      {aiModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center shrink-0">
                <Sparkles size={15} className="text-white"/>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-800">AI Fund Depletion Forecast</h3>
                <p className="text-xs text-slate-400">Analysing critical &amp; high-risk funds including upcoming shifts</p>
              </div>
              <button onClick={() => setAiModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400"><X size={16}/></button>
            </div>
            <div className="overflow-y-auto flex-1 p-6 space-y-3">
              {loadingAi && (
                <div className="flex flex-col items-center gap-3 py-10 text-slate-400">
                  <Loader2 size={28} className="animate-spin text-violet-500"/>
                  <p className="text-sm">AI is forecasting fund depletion…</p>
                </div>
              )}
              {!loadingAi && aiPreds?.map((p, i) => (
                <div key={i} className="border border-slate-200 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{p.clientName}</p>
                      {p.revisedDepletionDate && (
                        <p className="text-xs text-slate-500 mt-0.5">Revised depletion: <span className="font-medium text-slate-700">{p.revisedDepletionDate}</span></p>
                      )}
                      {p.daysUntilDepletion != null && (
                        <p className="text-xs text-slate-500">~{p.daysUntilDepletion} days remaining</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {p.willDepletEarly && (
                        <span className="text-[10px] font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Depletes Early</span>
                      )}
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        p.urgency === 'critical' ? 'bg-red-100 text-red-700' : p.urgency === 'high' ? 'bg-amber-100 text-amber-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>{p.urgency}</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                    <span className="font-medium text-slate-700">Recommended action: </span>{p.recommendation}
                  </p>
                </div>
              ))}
              {!loadingAi && aiPreds?.length === 0 && (
                <p className="text-center text-slate-400 py-8 text-sm">No predictions generated</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
